import { m } from '@hub/messages';
import { isAuthRedirectError, isTransientAuthError } from '@platform/api';
import { Button, ErrorPage, ErrorPageActions, ErrorPageMessage, ErrorPageTitle, LoadingOverlay } from '@platform/ui';
import { type ReactNode, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { DevErrorDetails } from '../components/DevErrorDetails';
import { ErrorEventCode } from '../components/ErrorEventCode';
import { reportBoundaryError } from './bootstrap/reportBoundaryError';

interface AppErrorBoundaryProps {
  children: ReactNode;
  onReset: () => void;
}

export function AppErrorBoundary({ children, onReset }: AppErrorBoundaryProps) {
  // Ref (not state) because onError fires synchronously before fallbackRender —
  // a ref is already populated when the fallback reads it, without an extra render cycle.
  const eventIdRef = useRef('');

  return (
    <ErrorBoundary
      fallbackRender={({ error, resetErrorBoundary }) => {
        // 401 backstop: the primary redirect lives at the fetch layer (registerUnauthorizedHandler);
        // rendering the generic ErrorPage would flash before the navigation lands. Match the
        // AuthedRouterProvider pre-router loading state so the brief window before
        // window.location.replace lands looks consistent with where the user is headed.
        if (isAuthRedirectError(error)) {
          return <LoadingOverlay open label={m.commons_loading_label()} labelHidden delay={0} />;
        }
        return (
          <ErrorPage>
            <ErrorPageTitle>{m.commons_error_page_title()}</ErrorPageTitle>
            <ErrorPageMessage>{m.commons_error_page_message_generic()}</ErrorPageMessage>
            <ErrorEventCode eventId={eventIdRef.current} />
            <DevErrorDetails error={error} />
            <ErrorPageActions>
              <Button size="lg" onClick={resetErrorBoundary}>
                {m.commons_error_page_back_to_home()}
              </Button>
            </ErrorPageActions>
          </ErrorPage>
        );
      }}
      onError={(error, info) => {
        // Auth redirect + transient session blips are handled at the fetch/query
        // layer (redirect / retry); don't report them as boundary crashes.
        if (isAuthRedirectError(error) || isTransientAuthError(error)) {
          return;
        }
        eventIdRef.current = reportBoundaryError(error, 'app', info.componentStack ?? undefined);
      }}
      onReset={onReset}
    >
      {children}
    </ErrorBoundary>
  );
}
